import React, { useState, useEffect, useContext } from 'react';
import { get as lodashGet, isEmpty } from 'lodash';
import moment from 'moment';
import { RestAPI as API } from '@aws-amplify/api-rest';
import { API_NAME } from '../../utils';
import { connect } from 'react-redux';

import {
  AffilsContext,
  getAffiliationName,
  getAffiliationByID
} from '../../helpers/affiliationsHelpers';
import {
  getApproverNames,
  getContributorNames
} from '../../helpers/get_approver_names';
import { getUserName } from '../../helpers/getUserName';
import { validateSecondaryContributors } from '../../helpers/otherContributorsHelper';

import { CurationReasonsForm } from '../common/CurationReasonsForm';
import LoadingSpinner from '../common/LoadingSpinner';
import Alert from '../common/Alert';
import { gdmPutParticipantsAndSetState } from '../../utilities/gdmUtilities';
import { useAmplifyAPIRequestRecycler } from '../../utilities/fetchUtilities';
import { setGdmAction } from '../../actions/gdmActions';

/**
 * Handle publishing/unpublishing and preview publishing/unpublishing a GDM classification in GCI
**/
function PublishGdm (props) {
  const requestRecycler = useAmplifyAPIRequestRecycler();
  const { affiliationsData } = useContext(AffilsContext);

  const [selectedSnapshot, setSelectedSnapshot] = useState(null);
  const [selectedProvisional, setSelectedProvisional] = useState(null);
  const [publishComment, setPublishComment] = useState(undefined);
  const [publishSubmitter, setPublishSubmitter] = useState(undefined);
  const [publishAffiliation, setPublishAffiliation] = useState(undefined);
  const [curationReasons, setCurationReasons] = useState([]);
  const [curationReasonsChecked, setCurationReasonsChecked] = useState([]);
  const [reasonsFormErrors, setReasonsFormErrors] = useState(null);
  const [isPublishPreview, setIsPublishPreview] = useState(false);
  const [showAlertMessage, setShowAlertMessage] = useState(false);
  const [alertType, setAlertType] = useState(null);
  const [alertClass, setAlertClass] = useState(null);
  const [alertMsg, setAlertMsg] = useState(null);
  const [submitBusy, setSubmitBusy] = useState(false); // Flag to indicate that the submit button is in a 'busy' state

  useEffect(() => {
    // Initialize publish reasons array
    const reasonsChecked = new Array(9).fill(false);
    setCurationReasonsChecked(reasonsChecked);

    // If both the data to identify a "selected" snapshot
    // (props.selectedSnapshotUUID and props.gdm) and a list of
    // snapshots to search (props.snapshots) exist,
    // then try to find the specified snapshot
    let selectedSnapshot = {};
    if (props.selectedSnapshotUUID && props.gdm && props.snapshots) {
      selectedSnapshot = props.snapshots.find(snapshot => (
        snapshot.PK === props.selectedSnapshotUUID &&
        (snapshot.gdm === props.gdm?.PK ||
          snapshot.resourceParent?.s3_archive_key &&
          snapshot.resourceParent.s3_archive_key.split('/', 3)[1] === props.gdm.PK)));
    } else if (props.snapshots) {
      // Otherwise, find the current approved snapshot (props.snapshots is assumed to be sorted)
      selectedSnapshot = props.snapshots.find(snapshot => snapshot.approvalStatus === 'Approved');
    }
    setSelectedSnapshot(selectedSnapshot);

    // If a "selected" snapshot exists, retrieve the "selected" provisional from it
    const provisionalSelected = selectedSnapshot?.resource ? selectedSnapshot.resource : {}
    setSelectedProvisional(provisionalSelected);

    // If it's a publish event then validate secondary contributors
    if (!provisionalSelected.publishClassification) {
      // If secondary contributor(s) and approver selection is not valid
      // then show error and disable preview button
      validateOtherContributors(provisionalSelected);
    }
  }, [])

  // Set state of comments set in textarea
  const handleCommentsChange = (value) => {
    setPublishComment(value);
  }

  /**
   * Method to check the selected secondary contributors and secondary
   * approver are valid in current snapshot classification
  **/
  const validateOtherContributors = (provisional) => {
    let valid = true;

    // Check if current GDM owner has been selected as secondary
    // contributor then show error message
    const contributorsList = provisional.classificationContributors || [];
    if (contributorsList.includes(props.gdm?.affiliation)) {
      valid = false;
    } else {
      // Check if current GDM owner GCEP has been selected as
      // secondary approver then show error message
      // But GDM owner VCEP is valid
      const gdmAffilData = getAffiliationByID(props.gdm?.affiliation, affiliationsData);
      if (gdmAffilData?.subgroups?.gcep?.id &&
        provisional.additionalApprover === gdmAffilData.subgroups.gcep.id) {
        valid = false;
      } else {
        // Check if selected secondary approver is also selected as
        // secondary contributor, show error message
        if (!validateSecondaryContributors(
          affiliationsData,
          contributorsList,
          provisional.additionalApprover
        )) {
          valid = false;
        }
      }
    }

    if (!valid) {
      showAlert(
        "danger",
        "mx-3",
        "The same affiliation cannot be listed more than once as contributing to a curation.  Please resave and approve the classification and change the Secondary Contributor and/or Secondary Approver selection."
      );
    }

    return valid;
  }

  /**
   * Method to handle previewing publish form
   * @param {object} e - The submitted event object
  **/
  const handlePreviewPublish = (e) => {
    e.preventDefault();
    const affiliation = props.affiliation;

    let formErrors;
    // If preview publish, check for errors; no need for preview unpublish.
    if (selectedProvisional && !selectedProvisional.publishClassification) {
      // If no curation reason related error, continue to preview publish
      formErrors = validateCurationReasons();
    }

    // If no error, continue to preview
    if (isEmpty(formErrors)) {
      const publishAffiliation = affiliation?.subgroups?.gcep?.id
        ? affiliation.subgroups.gcep.id
        : undefined;
      setPublishSubmitter(getUserName(props.auth));
      setPublishAffiliation(publishAffiliation);
      setIsPublishPreview(true);
    } else {
      setReasonsFormErrors(formErrors);
    }
  }

  /**
   * Method to handle resetting the publish form data
  **/
  const handleCancelPublish = () => {
    const resetCurationReasonsChecked = curationReasonsChecked.fill(false);
    setCurationReasons([]);
    setCurationReasonsChecked(resetCurationReasonsChecked);
    setPublishSubmitter(undefined);
    setPublishAffiliation(undefined);
    setPublishComment(undefined);
    setIsPublishPreview(false);
    requestRecycler.cancelAll();
  }

  /**
   * Method to handle editing the publish form data
  **/
  const handleEditPublish = () => {
    setIsPublishPreview(false);
  }

  /**
   * Method to show error alert
   * @param {string} alertType - The type of alert (added to class)
   * @param {string} alertClass - Custom classes for the alert
   * @param {object} alertMsg - The alert message
  **/
  const showAlert = (alertType, alertClass, alertMsg) => {
    setShowAlertMessage(true);
    setAlertType(alertType);
    setAlertClass(alertClass);
    setAlertMsg(alertMsg);
  }

  const setCheckboxState = (updatedCheckedState) => {
    setCurationReasonsChecked(updatedCheckedState);
  }

  const validateCurationReasons = () => {
    let errors = {};
    // Check for empty or missing values before moving to preview Publish
    if (!curationReasons || curationReasons.length < 1) {
      errors['error'] = "Please choose a reason for publishing before continuing";
    }
    return errors;
  }

  const clearCurationReasonsErrors = () => {
    setReasonsFormErrors(null);
  }

  const renderCurationReasonsPreview = () => {
    if (curationReasons && curationReasons.length > 0) {
      return curationReasons.map((reason, i) => {
        return (
          <dl className="inline-dl clearfix" key={i}>
            <dd>{reason}</dd>
          </dl>
        );
      });
    }
  }

  /**
   * Method to save curation reasons to snapshot before
   * publishing to data exchange
   * @param {Array} curationReasons - List of selected reason(s), selected by user in Publish panel
   * @param {Object} publishProvisional - The resource object within the snapshot
   * @param {Object} selectedSnapshot - The current snapshot to be updated and published
  **/
  const saveCurationReasonsToSelectedSnapshot = (curationReasons, publishProvisional, selectedSnapshot) => {
    publishProvisional['curationReasons'] = curationReasons;
    selectedSnapshot.resource = publishProvisional;
    const params = {body: {selectedSnapshot}}
    const url = `/snapshots/${selectedSnapshot.PK}?type=gdm`;

    return API.put(API_NAME, url, params).then(updatedSelectedSnapshot => {
      return Promise.resolve(updatedSelectedSnapshot);
    });
  }

  /**
   * Method to send publish/unpublish data to the Data Exchange gene_validity topic
   * @param {string} objType - The type of the data's source object (e.g. snapshot)
   * @param {string} objUUID - The UUID of the data's source object
  **/
  const publishToDataExchange = (objType, objUUID) => {
    let alertType = 'danger';
    let alertClass = 'mx-3';
    let alertMsg = (<span>Request failed; please try again in a few minutes or contact helpdesk: <a
    href="mailto:clingen-helpdesk@lists.stanford.edu">clingen-helpdesk@lists.stanford.edu</a></span>);

    return new Promise((resolve, reject) => {
      if (objType && objUUID) {
        const url = `/messaging/publish/${objUUID}`;
        API.get(API_NAME, url).then(result => {
          if (result.status === 'Success') {
            resolve(result);
          } else {
            console.log('Message delivery failure: %s', result.message);
            setSubmitBusy(false);
            showAlert(alertType, alertClass, alertMsg);
            reject(result);
          }
        }).catch(error => {
          console.log('Internal data retrieval error: %o', error);
          setSubmitBusy(false);
          showAlert(alertType, alertClass, alertMsg);
          reject(error);
        });
      } else {
        setSubmitBusy(false);
        showAlert(alertType, alertClass, alertMsg);
        reject(null);
      }
    });
  }

  /**
   * Method to send publish/unpublish classification tracking 
   * message to Data Exchange gene_validity_events topic
   * @param {object} provisional - provisional classification object
   * @param {string} publishSnapshotId - current publish/unpublish snapshot Id
  **/
  const sendTrackMsgToDataExchange = (provisional, publishSnapshotId) => {
    const publishSubmitter = props.auth;
    const status = provisional?.publishClassification ? 'published' : 'unpublished';
    const publishRole = provisional?.publishClassification ? ['publisher'] : ['unpublisher'];
    // Get all contributors
    const contributors = props.getContributors(publishSnapshotId);

    // Add this provisional publisher/unpublisher to contributors list
    if (publishSubmitter) {
      contributors.push({ 
        name: getUserName(publishSubmitter),
        id: lodashGet(publishSubmitter, 'PK', ''),
        email: lodashGet(publishSubmitter, 'email', ''),
        roles: publishRole
      });
    }

    // Create data object to be sent to Data Exchange
    const publishDate = provisional.publishDate ? provisional.publishDate : '';
    let uncData = props.setUNCData(provisional, status, publishDate, publishDate, publishSubmitter, contributors);

    // Post published/unpublished data to Data Exchange
    props.postTrackData(uncData).then(() => {
      console.log('Successfully sent %s data to Data Exchange for provisional %s at %s', status, provisional.PK, moment(publishDate).toISOString());
    }).catch(error => {
      console.log('Error sending %s data to Data Exchange for provisional %s at %s - Error: %o', status, provisional.PK, moment(publishDate).toISOString(), error);
    });
  }

  /**
   * Method to send full GDM snapshot data to Data Exchange
   * gene_validity_raw topic after provisional classification is
   * published/unpublished
   * @param {Object} snapshot - published/unpublished snapshot
  **/
  const publishGDMToDataExchange = (snapshot) => {
    // Post published GDM data snapshot to Data Exchange
    return new Promise((resolve, reject) => {
      // Check if snapshot has necessary data
      if (snapshot && snapshot.resource && snapshot.resourceParent) {
        // If gdm object is in snapshot.resourceParent, delete it
        if (snapshot.resourceParent.gdm) {
          delete snapshot.resourceParent['gdm'];
        }
        const params = { body: {snapshot} };
        API.post(API_NAME, '/messaging/publish-gdm', params).then(result => {
          if (result.status === 'Success') {
            console.log('Post full gdm succeeded: %o', result);
            resolve(result);
          } else {
            console.log('Post full gdm failed: %o', result);
            reject(result);
          }
        }).catch(error => {
          console.log('Post full gdm internal data retrieval error: %o', error);
          reject(error);
        });
      } else {
        console.log('Post full gdm Error: Missing expected data');
        reject({'message': 'Missing expected data'});
      }
    });
  }

  /**
   * Method to handle submitting the publish/unpublish form
   * @param {object} e - The submitted event object
  **/
  const submitForm = (e) => {
    e.preventDefault();
    e.stopPropagation();

    setSubmitBusy(true);
    if (selectedSnapshot?.item_type && selectedSnapshot?.PK) {
      let publishProvisional = selectedProvisional ? selectedProvisional : {};
      saveCurationReasonsToSelectedSnapshot(curationReasons, publishProvisional, selectedSnapshot).then(() => {
        publishToDataExchange(selectedSnapshot.item_type, selectedSnapshot.PK).then(() => {
          let currentProvisional = props.provisional ? props.provisional : {};
          const submissionTimestamp = new Date();

          // Update published (or unpublished) provisional with form data
          // (to be included when selected/published snapshot object is sent to the DB)
          publishProvisional.publishClassification = !selectedProvisional.publishClassification;
          publishProvisional.publishSubmitter = publishSubmitter;
          publishProvisional.publishAffiliation = publishAffiliation;
          publishProvisional.publishDate = moment(submissionTimestamp).toISOString();

          if (curationReasons && curationReasons.length) {
            publishProvisional['curationReasons'] = curationReasons;
          }
          if (publishComment && publishComment.length) {
            publishProvisional.publishComment = publishComment;
          } else if (publishProvisional.publishComment) {
            publishProvisional.publishComment = null;
          }

          // Additional provisional data that would otherwise be lost
          // (when snapshot object is sent to the DB)
          publishProvisional.last_modified = moment(submissionTimestamp).utc().format('Y-MM-DDTHH:mm:ss.SSSZ');
          publishProvisional.modified_by = lodashGet(props.auth, 'PK', null);

          // Check if the "selected" provisional is also the current one (using data other than the shared UUID)
          const isSelectedProvisionalCurrent = 
            publishProvisional?.provisionalDate === currentProvisional?.provisionalDate &&
            publishProvisional?.provisionalSubmitter === currentProvisional?.provisionalSubmitter &&
            publishProvisional?.approvalDate === currentProvisional?.approvalDate &&
            publishProvisional?.approvalSubmitter === currentProvisional?.approvalSubmitter &&
            publishProvisional?.affiliation === currentProvisional?.affiliation;
          // Only update current provisional object with form data when publish event is on current approved snapshot
          if (isSelectedProvisionalCurrent) {
            currentProvisional.publishClassification = !props.provisional.publishClassification;
            currentProvisional.publishSubmitter = publishSubmitter;
            currentProvisional.publishAffiliation = publishAffiliation;
            currentProvisional.publishDate = moment(submissionTimestamp).toISOString();

            if (curationReasons && curationReasons.length) {
              currentProvisional['curationReasons'] = curationReasons;
            }
            if (publishComment && publishComment.length) {
              currentProvisional.publishComment = publishComment;
            } else if (currentProvisional.publishComment) {
              currentProvisional.publishComment = null;
            }
            currentProvisional.modified_by = lodashGet(props.auth, 'PK', null);

            const url = '/provisional-classifications/' + props.provisional.PK;
            const params = {body: {currentProvisional}};

            // Send updated current provisional object to the DB
            API.put(API_NAME, url, params).then(responseProvisional => {
              // Send publish GDM provisional track message to Data Exchange
              sendTrackMsgToDataExchange(responseProvisional, selectedSnapshot.PK);
              // Only update provisional state object when publish event is on current approved snapshot
              props.updateProvisionalObj(responseProvisional);
              return Promise.resolve(responseProvisional);
            }).catch(error => {
              console.log('Updating provisional error = : %o', error);
            });
          } else {
            // If not current provisional classification,
            // should not update provisional.
            // Just send publish track message for publishProvisional
            sendTrackMsgToDataExchange(publishProvisional, selectedSnapshot.PK);
          }

          // Create selected/published snapshot object, updated with publish event data
          const publishSnapshot = {
            resourceId: publishProvisional.PK,
            resourceType: selectedSnapshot.resourceType,
            approvalStatus: selectedSnapshot.approvalStatus,
            resource: publishProvisional,
            resourceParent: selectedSnapshot.resourceParent,
            disease: selectedSnapshot.disease ? selectedSnapshot.disease : null,
            diseaseTerm: selectedSnapshot.diseaseTerm ? selectedSnapshot.diseaseTerm : null,
            modeInheritance: selectedSnapshot.modeInheritance ? selectedSnapshot.modeInheritance : null,
            modeInheritanceAdjective: selectedSnapshot.modeInheritanceAdjective ? selectedSnapshot.modeInheritanceAdjective : null,
            associatedSnapshot: selectedSnapshot.associatedSnapshot,
            date_created: selectedSnapshot.date_created,
            modified_by: lodashGet(props.auth, "PK", null),
          };

          let url = '/snapshots/' + selectedSnapshot.PK + '?type=gdm';
          let params = {body: {publishSnapshot}};

          // Send updated selected/published snapshot object to the DB
          API.put(API_NAME, url, params).then(responseSnapshot => {
            props.updateSnapshotList(responseSnapshot);
            return Promise.resolve(responseSnapshot);
          }).then(resultSnapshot => {
            // Send published GDM data (snapshot) to Data Exchange
            publishGDMToDataExchange(resultSnapshot);

            // When publish event is a publish, automatically unpublish a previously-published snapshot (if one exists)
            if (publishProvisional.publishClassification) {
              const previouslyPublishedSnapshot = props.snapshots ? props.snapshots.find(snapshot => (snapshot.resource &&
                snapshot.resource.publishClassification && snapshot.PK !== selectedSnapshot.PK)) : {};

              if (previouslyPublishedSnapshot && previouslyPublishedSnapshot.resource) {
                // Update previously-published snapshot with automatic unpublish data
                previouslyPublishedSnapshot.resource.publishComment = 'Classification previously published by ' +
                  previouslyPublishedSnapshot.resource.publishSubmitter + ' on ' +
                  moment(previouslyPublishedSnapshot.resource.publishDate).format('YYYY MMM DD') +
                  (previouslyPublishedSnapshot.resource.publishComment ? ' with the following comment: ' +
                  previouslyPublishedSnapshot.resource.publishComment : '');
                previouslyPublishedSnapshot.resource.publishClassification = false;
                previouslyPublishedSnapshot.resource.isRepublish = false;
                previouslyPublishedSnapshot.resource.publishSubmitter = publishProvisional.publishSubmitter +
                  ' (automatic due to publication of another classification)';
                previouslyPublishedSnapshot.resource.publishAffiliation = publishProvisional.publishAffiliation;
                previouslyPublishedSnapshot.resource.publishDate = publishProvisional.publishDate;
                previouslyPublishedSnapshot.resource.last_modified = publishProvisional.last_modified;
                previouslyPublishedSnapshot.resource.modified_by = publishProvisional.modified_by;

                const autoUnpublishSnapshot = {
                  resourceId: previouslyPublishedSnapshot.resource.PK,
                  resourceType: previouslyPublishedSnapshot.resourceType,
                  approvalStatus: previouslyPublishedSnapshot.approvalStatus,
                  resource: previouslyPublishedSnapshot.resource,
                  resourceParent: previouslyPublishedSnapshot.resourceParent,
                  disease: previouslyPublishedSnapshot.disease ? previouslyPublishedSnapshot.disease : null,
                  diseaseTerm: previouslyPublishedSnapshot.diseaseTerm ? previouslyPublishedSnapshot.diseaseTerm : null,
                  modeInheritance: previouslyPublishedSnapshot.modeInheritance ? previouslyPublishedSnapshot.modeInheritance : null,
                  modeInheritanceAdjective: previouslyPublishedSnapshot.modeInheritanceAdjective ? previouslyPublishedSnapshot.modeInheritanceAdjective : null,
                  associatedSnapshot: previouslyPublishedSnapshot.associatedSnapshot,
                  date_created: previouslyPublishedSnapshot.date_created,
                  modified_by: lodashGet(props.auth, "PK", null),
                };

                url = '/snapshots/' + previouslyPublishedSnapshot.PK + '?type=gdm';
                params = {body: {autoUnpublishSnapshot}};

                // Send updated (unpublished) previously-published snapshot object to the DB
                API.put(API_NAME, url, params).then(responseSnapshot => {
                  props.updateSnapshotList(responseSnapshot);
                  return Promise.resolve(responseSnapshot);
                }).then(resultSnapshot => {
                  // Send unpublish GDM provisional track message to Data Exchange
                  if (resultSnapshot && resultSnapshot.resource) {
                    sendTrackMsgToDataExchange(resultSnapshot.resource, resultSnapshot.PK);
                  }
                }).catch(error => {
                  console.log('Automatic unpublishing snapshot error = : %o', error);
                });
              }
            }

            // Clear publish-related URL query parameters and state data
            props.clearPublishState();
          }).catch(error => {
            console.log('Publishing snapshot error = : %o', error);
          }).then(() => 
            // update gdm participants && also get the latest provisional classifications and snapshots
            gdmPutParticipantsAndSetState({
              requestRecycler: requestRecycler,
              gdm: props.gdm,
              auth: props.auth,
              setGdm: props.setGdmAction
            })
          );
        }).catch(error => {
          console.log('Classification publication error = : %o', error);
        });
      }).catch(error => {
        console.log('Updating selectedSnapshot error = : %o', error);
      });
    } else {
      setSubmitBusy(false);
      console.log("No selected snapshot to be published.");
    }
  }

  /**
   * Method to render preview publish/unpublish form
  **/
  const renderPublishPreview = () => {
    return(
      <div className="publish-preview">
        <div className="row">
          <div className="col-sm-4">
            <h5><strong>ClinGen Affiliation:</strong> {affiliation ? getAffiliationName(affiliation, affiliationsData, 'gcep') : null}</h5>
            <h5><strong>Classification Approver:</strong> {additionalApprover ? getApproverNames(additionalApprover, affiliationsData) : null}</h5>
            <dl className="inline-dl clearfix">
              <dt><span>Classification Contributor(s):</span></dt>
              <dd>{classificationContributors ? getContributorNames(classificationContributors, affiliationsData).join(', ') : null}</dd>
            </dl>
            <div className="publish-submitter">
              <dl className="inline-dl clearfix">
                <dt><span>Classification {publishEventLower} by:</span></dt>
                <dd>{publishSubmitter ? publishSubmitter : null}</dd>
              </dl>
            </div>
          </div>
          <div className="col-sm-4">
            <h5><strong>Date {publishEventLower}: {publishDate}</strong></h5>
          </div>
          <div className="col-sm-4">
            <dl className="inline-dl clearfix preview-publish-comment text-pre-wrap">
              <h5><span className="text-pre-wrap"><strong>Comments about publishing reasons (optional, for internal use only):</strong></span></h5>
              {publishComment ? publishComment : null}
            </dl>
          </div>
        </div>
        {publishEvent === "Publish" ?
          <div className="row">
            <div className="col-sm-6">
              <h4>Reason(s) for Publishing Classification</h4>
                {renderCurationReasonsPreview()}
            </div>
          </div>
        : null}
        <div className="alert alert-warning publish-preview-note">
          <i className="icon icon-exclamation-circle"></i> This is a Preview only; you must still {publishEvent} this Classification.
        </div>

        <div className="col-md-12 publish-form-buttons-wrapper">
          <div className="button-group">
            <button
              type="button"
              className="btn btn-light btn-lg mr-1"
              onClick={handleCancelPublish}
              disabled={submitBusy}>
              Cancel {publishEvent}
            </button>
            <button
              type="button"
              className="btn btn-info btn-lg mr-1"
              onClick={handleEditPublish}
              disabled={submitBusy}>
              Edit <i className="icon icon-pencil"></i>
            </button>
            <button
              type="submit"
              className="btn btn-primary btn-lg ml-uto"
              disabled={submitBusy}>
              {submitBusy ? <LoadingSpinner size="1x"/> : null}
              {publishEvent} <i className="icon icon-check-square-o"></i>
            </button>
            {showAlertMessage && (
              <Alert
                type={alertType}
                value={alertMsg}
                className={alertClass}
                dismissible
              />
            )}
          </div>
        </div>
      </div>
    );
  }

  /**
   * Method to render edit publish/unpublish form
  **/
  const renderPublishEdit = () => {
    return (
      <div className="publish-edit">
        <div className="row">
          <div className="col-sm-6">
            <h5><strong>ClinGen Affiliation: </strong>
              {affiliation ? getAffiliationName(affiliation, affiliationsData, 'gcep') : null}
            </h5>
            <h5><strong>Entered by: </strong>
              <span className="text-muted">Current curator's name will be entered upon {publicationEventLower}</span>
            </h5>
            <h5><strong>Date {publishEventLower}: </strong>
              <span className="text-muted">Current date and time will be entered upon {publicationEventLower}</span>
            </h5>
          </div>
          <div className="col-sm-6">
            <div className="publish-comments">
              <h5><strong>Comments about publishing reasons (optional, for internal use only):</strong></h5>
              <textarea placeholder="Enter additional comments..."
                value={publishComment} rows="5"
                onChange={e => handleCommentsChange(e.currentTarget.value)}
                className="form-control"
              />
            </div>
          </div>
          {publishEvent === "Publish" ?
            <div className="col-sm-6">
              <CurationReasonsForm
                curationReasons={curationReasons}
                checkedState={curationReasonsChecked}
                setCurationReasons={setCurationReasons}
                setCheckedState={setCheckboxState}
                selectedResourceType="gdm"
                reasonsFormErrors={reasonsFormErrors}
                clearErrors={clearCurationReasonsErrors}
              />
            </div>
          : null}
          {showAlertMessage && (
            <Alert
              type={alertType}
              value={alertMsg}
              className={alertClass}
            />
          )}
        </div>
        <div className="col-md-12 publish-form-buttons-wrapper">
          <div className="button-group">
            <button
              type="button"
              className="btn btn-primary"
              disabled={showAlertMessage}
              onClick={handlePreviewPublish}>
              Preview {publishEvent}
            </button>
          </div>
        </div>
      </div>
    );
  }

  const publishDate = moment().format('YYYY MMM DD, h:mm a');
  const provisional = selectedProvisional;
  const additionalApprover = provisional?.additionalApprover ? provisional.additionalApprover : null;
  const classificationContributors = provisional?.classificationContributors ? provisional.classificationContributors : null;
  const affiliation = provisional?.affiliation ? provisional.affiliation : (props.affiliation ? props.affiliation : null);
  const publishEvent = provisional?.publishClassification ? 'Unpublish' : 'Publish';
  const publishEventLower = provisional?.publishClassification ? 'unpublished' : 'published';
  const publicationEventLower = provisional?.publishClassification ? 'unpublication' : 'publication';
        
  return (
    <div className="publish-approval-panel-content">
      <form onSubmit={submitForm}>
        {isPublishPreview ?
          renderPublishPreview()
        :
          renderPublishEdit()
        }
      </form>
    </div>
  )
}

const mapStateToProps = state => ({
  auth: state.auth
});

const mapDispatchToProps = dispatch => ({
    setGdmAction: gdm => dispatch(setGdmAction(gdm)),
});

export default connect(mapStateToProps, mapDispatchToProps)(PublishGdm);
